import React from "react";
import {
  Download,
  Eye,
  File,
  Paperclip,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { SectionCard } from "../UI/SectionCard";
import { PreviewableExtensions } from "../../constants/formOptions";
import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_FILES_PER_ZONE,
  ATTACHMENT_MAX_FILE_SIZE_MB,
} from "../../constants/containerOrderConstants";
import {
  canRemoveAttachment,
  extensionOf,
  screenAttachments,
  uploaderName,
  type AttachmentViewer,
} from "../../utils/containerAttachments";
import type {
  AttachmentCategory,
  ContainerLineAttachment,
} from "../../types/containerOrders";

interface ContainerLineAttachmentsSectionProps {
  attachments: ContainerLineAttachment[];
  pendingGeneral: File[];
  pendingCustoms: File[];
  onPendingChange: (category: AttachmentCategory, files: File[]) => void;
  onDownload: (
    attachment: ContainerLineAttachment,
    previewOnly?: boolean,
  ) => void;
  onDelete: (attachment: ContainerLineAttachment) => void;
  /** The row currently downloading or being deleted. */
  busyId: string | null;
  /** Completed or cancelled container, or read mode — shown, not added to. */
  readonly: boolean;
  viewer: AttachmentViewer;
}

/**
 * The documents filed against one container.
 *
 * The order's two zones, one level down: general commercial paperwork and
 * customs paperwork, kept apart at upload time so a customs audit never has to
 * identify its own documents by filename. An order's attachments describe the
 * booking — the contract, the master B/L — while these describe *this box*: its
 * gate receipt, its weighbridge ticket, the photograph of its seal. Neither list
 * stands in for the other.
 *
 * The one thing the order has no equivalent of is the second author. Both the
 * office and the assigned driver file documents here, into one list, and each
 * sees everything the other filed — dispatch has to be able to read what came
 * back from the terminal, and the driver has to be able to open the booking
 * paperwork at the gate. What is not shared is removal: a document belongs to
 * the side that filed it (see `canRemoveAttachment`). So every row says who
 * uploaded it and which side they were on, because a row with no bin icon has to
 * read as someone else's document rather than as a broken button.
 *
 * Files picked here are pending until Save, exactly as on the order form: they
 * ride along with the container's own save, and a rejected save takes them with
 * it rather than leaving half an edit stored.
 */
export function ContainerLineAttachmentsSection({
  attachments,
  pendingGeneral,
  pendingCustoms,
  onPendingChange,
  onDownload,
  onDelete,
  busyId,
  readonly,
  viewer,
}: ContainerLineAttachmentsSectionProps) {
  const { t } = useTranslation("containerOrders");

  /** Screen a pick against the shared rules and say what was dropped. */
  const acceptable = (selected: File[], room: number) => {
    const { kept, rejected } = screenAttachments(selected, room);

    for (const rejection of rejected) {
      toast.error(
        rejection.reason === "type"
          ? t("attachments.rejectedType", { name: rejection.name })
          : rejection.reason === "size"
            ? t("attachments.rejectedSize", {
                name: rejection.name,
                limit: ATTACHMENT_MAX_FILE_SIZE_MB,
              })
            : t("attachments.rejectedCount", {
                name: rejection.name,
                limit: ATTACHMENT_MAX_FILES_PER_ZONE,
              }),
      );
    }

    return kept;
  };

  const pendingFor = (category: AttachmentCategory) =>
    category === "customs" ? pendingCustoms : pendingGeneral;

  const pickerFor =
    (category: AttachmentCategory) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files || []);
      event.target.value = "";

      // Screened outside any state updater: `acceptable` raises a toast per
      // rejected file, and React re-runs updaters in StrictMode — which would
      // show every one of those warnings twice.
      const current = pendingFor(category);
      const kept = acceptable(
        selected,
        ATTACHMENT_MAX_FILES_PER_ZONE - current.length,
      );
      if (!kept.length) return;

      onPendingChange(category, [...current, ...kept]);
    };

  const removePending = (category: AttachmentCategory, index: number) =>
    onPendingChange(
      category,
      pendingFor(category).filter((_, i) => i !== index),
    );

  const isPreviewable = (name: string) =>
    PreviewableExtensions.includes(extensionOf(name));

  /** One drop zone plus the documents already in (or headed for) its category. */
  const zone = (category: AttachmentCategory) => {
    const isCustoms = category === "customs";
    // Legacy rows predate the category field — treat untagged as general.
    const stored = attachments.filter((a) =>
      isCustoms ? a.category === "customs" : a.category !== "customs",
    );
    const pending = pendingFor(category);

    const Icon = isCustoms ? ShieldCheck : Upload;

    return (
      <div className="space-y-2">
        <label
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-5 text-center transition-colors ${
            isCustoms
              ? "border-honey/50 bg-honey/8 hover:border-honey"
              : "border-dark/15 bg-bone/60 hover:border-dark/30"
          } ${readonly ? "pointer-events-none opacity-60" : ""}`}
        >
          <Icon
            className={`mb-1 h-5 w-5 ${isCustoms ? "text-honey" : "text-mist"}`}
          />
          <span
            className={`text-[12px] font-semibold ${
              isCustoms ? "text-amber-900" : "text-ink"
            }`}
          >
            {t(
              isCustoms
                ? "attachments.customsLabel"
                : "attachments.generalLabel",
            )}
          </span>
          <span
            className={`text-[10.5px] leading-snug ${
              isCustoms ? "text-amber-700" : "text-mist"
            }`}
          >
            {t(
              isCustoms
                ? "lineAttachments.customsHint"
                : "lineAttachments.generalHint",
            )}
          </span>
          <span className="mt-1 text-[10px] text-mist">
            {t("attachments.limitsHint", {
              count: ATTACHMENT_MAX_FILES_PER_ZONE,
              size: ATTACHMENT_MAX_FILE_SIZE_MB,
            })}
          </span>
          <input
            type="file"
            multiple
            // A hint to the OS dialog only; `acceptable` is what enforces it.
            accept={ATTACHMENT_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",")}
            className="hidden"
            disabled={readonly}
            onChange={pickerFor(category)}
          />
        </label>

        {(stored.length > 0 || pending.length > 0) && (
          <ul className="divide-y divide-dark/8 rounded-md border border-dark/10">
            {stored.map((attachment) => {
              const fromDriver = attachment.uploadedByRole === "driver";
              const mine = canRemoveAttachment(attachment, viewer);
              const who = uploaderName(attachment);

              return (
                <li key={attachment._id} className="px-2.5 py-1.5">
                  <div className="flex items-center gap-2">
                    <File
                      className={`h-3.5 w-3.5 shrink-0 ${
                        isCustoms ? "text-honey" : "text-mist"
                      }`}
                    />
                    <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink">
                      {attachment.originalName}
                    </span>
                    <span className="shrink-0 text-[10.5px] text-mist">
                      {(attachment.size / 1024).toFixed(0)} KB
                    </span>

                    {isPreviewable(attachment.originalName) && (
                      <button
                        type="button"
                        disabled={busyId === attachment._id}
                        onClick={() => onDownload(attachment, true)}
                        className="rounded p-1 text-mist hover:bg-bone hover:text-ink"
                        title={t("attachments.preview")}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    )}

                    <button
                      type="button"
                      disabled={busyId === attachment._id}
                      onClick={() => onDownload(attachment)}
                      className="rounded p-1 text-mist hover:bg-bone hover:text-ink"
                      title={t("attachments.download")}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>

                    {/* Absent, not disabled, when the document is someone
                        else's: a greyed bin invites a click that will never
                        work, and the line below already says whose it is. */}
                    {!readonly && mine && (
                      <button
                        type="button"
                        disabled={busyId === attachment._id}
                        onClick={() => onDelete(attachment)}
                        className="rounded p-1 text-signal hover:bg-signal/10"
                        title={t("attachments.delete")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Who filed it, and from which side. The badge is what makes
                      a missing bin legible as a rule rather than a bug. */}
                  <div className="mt-0.5 flex items-center gap-1.5 pl-5.5 text-[10px] text-mist">
                    <span
                      className={`rounded px-1 py-px font-semibold uppercase tracking-wide ${
                        fromDriver
                          ? "bg-copper/12 text-copper"
                          : "bg-bone text-mist"
                      }`}
                    >
                      {t(
                        fromDriver
                          ? "lineAttachments.byDriver"
                          : "lineAttachments.byOffice",
                      )}
                    </span>
                    <span className="truncate">
                      {who || t("lineAttachments.unknownUploader")}
                    </span>
                  </div>
                </li>
              );
            })}

            {pending.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 px-2.5 py-1.5"
              >
                <File className="h-3.5 w-3.5 shrink-0 text-mist" />
                <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink">
                  {file.name}
                </span>
                <span className="shrink-0 rounded bg-bone px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-mist">
                  {t("attachments.pending")}
                </span>
                <button
                  type="button"
                  onClick={() => removePending(category, index)}
                  className="rounded p-1 text-signal hover:bg-signal/10"
                  title={t("attachments.delete")}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <SectionCard icon={Paperclip} title={t("lineAttachments.title")}>
      <div className="space-y-2">
        <p className="text-[11px] text-mist">{t("lineAttachments.hint")}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {zone("general")}
          {zone("customs")}
        </div>
      </div>
    </SectionCard>
  );
}

export default ContainerLineAttachmentsSection;
